// src/components/RewardsProgress.tsx
import React, { useEffect, useMemo } from "react";
import styled, { css } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../app/store";
import { fetchDepositDeltaRewards } from "../features/rewardSlice";
import { FaGift, FaBoxOpen } from "react-icons/fa";
import noImageAvailable from "../assets/no_image.png";

/* ---------- Config (static thresholds) ---------- */
type TierKey = "Bronze Box" | "Silver Box" | "Gold Box" | "Diamond Box" | "Platinum Box" | "Master Box";
type Tier = { name: TierKey; threshold: number; img_o?: string; img_c?: string };

const TIERS: Tier[] = [
  { name: "Bronze Box",   threshold: 20,   img_o: "/assets/rewards/bronze_reward_open.png",   img_c: "/assets/rewards/bronze_reward_close.png" },
  { name: "Silver Box",   threshold: 50,   img_o: "/assets/rewards/silver_reward_open.png",   img_c: "/assets/rewards/silver_reward_close.png" },
  { name: "Gold Box",     threshold: 100,  img_o: "/assets/rewards/gold_reward_open.png",     img_c: "/assets/rewards/gold_reward_close.png" },
  { name: "Diamond Box",  threshold: 200,  img_o: "/assets/rewards/diamond_reward_open.png",  img_c: "/assets/rewards/diamond_reward_close.png" },
  { name: "Platinum Box", threshold: 500,  img_o: "/assets/rewards/platinum_reward_open.png", img_c: "/assets/rewards/platinum_reward_close.png" },
  { name: "Master Box",   threshold: 1000, img_o: "/assets/rewards/master_reward_open.png",   img_c: "/assets/rewards/platinum_reward_close.png" },
];

/* ---------- Styles ---------- */
const Wrap = styled.section`
  width: 100%;
  padding: 16px;
  background: #071616;
  border-radius: 10px;
  color: #e8f4f4;
`;
const Title = styled.h3`
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 700;
`;
const Track = styled.div`
  display: grid;
  grid-template-columns: repeat(${TIERS.length * 2 - 1}, minmax(0, 1fr));
  align-items: center;
  gap: 12px;

  @media (max-width: 640px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;
const Node = styled.div`
  display: grid;
  place-items: center;
  text-align: center;

  @media (max-width: 640px) {
    width: 100%;
  }
`;
const GiftImg = styled.img<{ $dim?: boolean }>`
  width: 64px;
  height: 64px;
  object-fit: contain;
  filter: ${({ $dim }) => ($dim ? "grayscale(100%) opacity(0.6)" : "none")};

  @media (max-width: 640px) {
    width: 56px;
    height: 56px;
  }
`;
const GiftIcon = styled.div<{ $dim?: boolean }>`
  font-size: 56px;
  ${({ $dim }) =>
    $dim &&
    css`
      opacity: 0.5;
      filter: grayscale(100%);
    `}
`;
const Label = styled.div`
  margin-top: 6px;
  font-weight: 700;
`;
const Sub = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #b4c9c9;
`;
const Segment = styled.div`
  height: 8px;
  background: rgba(58, 180, 160, 0.25);
  border-radius: 999px;
  position: relative;
  overflow: hidden;

  @media (max-width: 640px) {
    width: 6px;
    height: 64px;
  }
`;


const SegmentFill = styled.div<{ $pct: number }>`
  position: absolute;
  inset: 0;
  transform-origin: left center;
  background: linear-gradient(90deg, #3ab4a0, #59e0cc);
  width: ${({ $pct }) => Math.max(0, Math.min(100, $pct))}%;

  @media (max-width: 640px) {
    transform-origin: top center;                 /* grow downward */
    width: 100%;
    height: ${({ $pct }) => Math.max(0, Math.min(100, $pct))}%;
    top: 0;                                       /* anchor to top */
    bottom: auto;
    background: linear-gradient(180deg, #3ab4a0, #59e0cc); /* vertical gradient */
  }
`;

const Marker = styled.div`
  position: absolute;
  top: -20px;
  right: 0;
  transform: translateX(50%);
  font-size: 12px;
  color: #9ee8dc;
  @media (max-width: 640px) {
    top: auto;
    left: 10px;
    right: auto;
    transform: none;
    bottom: 0;
  }
`;

/* ---------- Helpers ---------- */
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
const fmtMoney = (n: number) =>
  n.toLocaleString(undefined, { maximumFractionDigits: 0, minimumFractionDigits: 0 });

/* ---------- Component ---------- */
const RewardsProgress: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();

  const depositDelta = useSelector((s: RootState) => s.rewards.depositDeltaData);
  const deltaStatus = useSelector((s: RootState) => s.rewards.depositDeltaStatus);

  useEffect(() => {
    if (deltaStatus === "idle") dispatch(fetchDepositDeltaRewards());
  }, [dispatch, deltaStatus]);

  const { current, deliveredByName, activeSegmentIdx, segments } = useMemo(() => {
    const current = depositDelta?.deposit_delta_usd ?? 0;

    // Map reward_name -> delivered boolean
    const deliveredByName = new Map<string, boolean>();
    for (const r of depositDelta?.rewards_unlocked ?? []) {
      deliveredByName.set(r.reward_name, !!r.delivered);
    }

    const segments = TIERS.slice(0, -1).map((_t, i) => {
      const prev = i === 0 ? 0 : TIERS[i].threshold;
      const next = TIERS[i + 1].threshold;
      const pct = clamp01((current - prev) / (next - prev)) * 100;
      return { prev, next, pct };
    });

    let activeSegmentIdx = segments.findIndex((seg) => current >= seg.prev && current < seg.next);
    if (activeSegmentIdx === -1) activeSegmentIdx = current < segments[0].prev ? 0 : segments.length - 1;

    return { current, deliveredByName, activeSegmentIdx, segments };
  }, [depositDelta]);

  return (
    <Wrap>
      <Title>Reward Boxes</Title>

      <Track>
        {TIERS.map((tier, i) => {
          const delivered = deliveredByName.get(tier.name) === true;
          const imgSrc = delivered ? tier.img_o : tier.img_c;
          const dim = !delivered && current < tier.threshold; // subtle dim if not delivered and not reached threshold

          return (
            <React.Fragment key={tier.name}>
              <Node>
                {imgSrc ? (
                  <GiftImg
                    src={imgSrc}
                    alt={tier.name}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = noImageAvailable;
                    }}
                    $dim={dim}
                  />
                ) : (
                  <GiftIcon $dim={dim}>{delivered ? <FaGift /> : <FaBoxOpen />}</GiftIcon>
                )}
                <Label>{tier.name.replace(" Box", "")}</Label>
                <Sub>Deposit ${tier.threshold}</Sub>
              </Node>

              {i < TIERS.length - 1 && (
                <Segment>
                  <SegmentFill $pct={segments[i].pct} />
                  {i === activeSegmentIdx && <Marker>${fmtMoney(current)}</Marker>}
                </Segment>
              )}
            </React.Fragment>
          );
        })}
      </Track>
    </Wrap>
  );
};

export default RewardsProgress;
